import { LogOut, Menu, Settings, Shield, User } from "lucide-react";
import { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router";

import { useAuth } from "~/auth/auth-context";
import { hasAdminAccess } from "~/auth/ward-roles";
import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import { Toaster } from "~/components/ui/sonner";
import { roleLabel } from "~/lib/roles";

// The chrome around every authenticated route: a top bar with the app name,
// the signed-in user's identity, and logout. On <sm the identity + logout
// collapse into a hamburger-triggered drawer; on >=sm they sit inline.
export function AppShell(): JSX.Element {
  const { user, logout, isSuperAdmin, wards } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const name = user?.name ?? user?.email ?? "Account";
  const role = user?.role ? roleLabel(user.role) : null;
  // Admin entry point shows only to super-admins or ward admins (v2 §3); the
  // screens it links to land in later epics (E1.2/E2.2/E3.3).
  const showAdmin = hasAdminAccess(isSuperAdmin, wards);

  const handleLogout = async (): Promise<void> => {
    setMenuOpen(false);
    try {
      await logout();
    } finally {
      // Navigate even if signOut rejects — a failed logout must not strand the
      // user on an authenticated screen.
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between px-4">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            HandoverPro
          </Link>

          {/* Desktop: identity + admin + logout inline */}
          <div className="hidden items-center gap-4 sm:flex">
            <div className="text-right leading-tight">
              <p className="text-sm font-medium">{name}</p>
              {role && <p className="text-xs text-muted-foreground">{role}</p>}
            </div>
            {showAdmin && (
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin">
                  <Shield />
                  Admin
                </Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="sm">
              <Link to="/profile">
                <User />
                Profile
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/settings">
                <Settings />
                Settings
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => void handleLogout()}>
              <LogOut />
              Log out
            </Button>
          </div>

          {/* Mobile: hamburger opens a drawer with the same actions */}
          <div className="sm:hidden">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader className="text-left">
                  <SheetTitle>{name}</SheetTitle>
                  {role && <SheetDescription>{role}</SheetDescription>}
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-2">
                  {showAdmin && (
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/admin" onClick={() => setMenuOpen(false)}>
                        <Shield />
                        Admin
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/profile" onClick={() => setMenuOpen(false)}>
                      <User />
                      Profile
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/settings" onClick={() => setMenuOpen(false)}>
                      <Settings />
                      Settings
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => void handleLogout()}>
                    <LogOut />
                    Log out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <Toaster />
    </div>
  );
}
